import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getUserRoles, hasRole } from '@quotation/domain';
import type {
  AuditEvent,
  ChatMessage,
  PriceCatalogVersion,
  Quote,
  QuoteApproval,
  RequirementDraft,
  User,
} from '@quotation/domain';
import { api } from './api';
import {
  cacheOfflineDraft,
  cachePublishedCatalog,
  calculateOfflineQuote,
  getCachedCatalog,
  getOfflineDrafts,
} from './offline';
import { buildQuickQuote, type QuickQuotePrice, type QuickQuoteResult } from './quick-quote';

type View = 'dashboard' | 'workspace' | 'quotes' | 'catalog' | 'approvals' | 'audit';

interface RequirementResponse {
  requirement: RequirementDraft;
  messages: ChatMessage[];
  engine: 'model' | 'rules';
  warning?: string;
}

interface QuoteDetail {
  quote: Quote;
  audit: AuditEvent[];
}

interface UploadedAttachment {
  id: string;
  fileName: string;
  warning?: string;
}

const STATUS_LABELS: Record<Quote['status'], string> = {
  draft: '报价草稿',
  pending_pricing: '待报价管理员',
  pending_boss: '待老板审批',
  approved: '审批通过',
  rejected: '已驳回',
  published: '正式报价',
  superseded: '历史版本',
};

const ROLE_LABELS: Record<User['role'], string> = {
  sales: '销售',
  pricing_admin: '报价管理员',
  boss: '老板',
  system_admin: '系统管理员',
};

function roleLabel(user: User): string {
  return getUserRoles(user).map((role) => ROLE_LABELS[role]).join(' / ');
}

function formatMoney(minor: number): string {
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    minimumFractionDigits: 2,
  }).format(minor / 100);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
}

async function download(path: string): Promise<string> {
  const response = await fetch(api.downloadUrl(path), { credentials: 'include' });
  if (!response.ok) {
    let message = `下载失败：HTTP ${response.status}`;
    try {
      const body = await response.json() as { message?: string };
      if (body.message) message = body.message;
    } catch {
      // Keep the HTTP error message when the response is not JSON.
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const fallbackName = path.endsWith('.xlsx') ? '报价单.xlsx' : '报价单.pdf';
  const filename = encodedName ? decodeURIComponent(encodedName) : fallbackName;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return filename;
}

const AUDIT_LABELS: Record<string, string> = {
  'quote.created': '生成报价',
  'quote.submitted': '提交审批',
  'quote.approved': '审批通过',
  'quote.rejected': '审批驳回',
  'quote.option_added': '增加选配',
  'quote.revision_created': '创建修订版',
  'quote.hidden_by_sales': '销售整理删除',
  'quote.deleted': '永久删除',
  'quote.pdf_exported': '导出PDF',
  'quote.xlsx_exported': '导出Excel',
};

function auditLabel(action: string): string {
  return AUDIT_LABELS[action] ?? action;
}

function previewPdf(path: string): void {
  const url = api.downloadUrl(path);
  const opened = window.open(url, '_blank', 'noopener');
  if (!opened) window.location.assign(url);
}

function ThemeToggle() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => document.documentElement.dataset.publicTheme === 'light' ? 'light' : 'dark');

  useEffect(() => {
    document.documentElement.dataset.publicTheme = theme;
    localStorage.setItem('quotation.publicTheme', theme);
  }, [theme]);

  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      className="theme-toggle"
      type="button"
      onClick={() => setTheme(nextTheme)}
      aria-label={`切换到${nextTheme === 'light' ? '浅色' : '深色'}背景`}
      title={`切换到${nextTheme === 'light' ? '浅色' : '深色'}背景`}
    >
      <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
      {nextTheme === 'light' ? '浅色' : '深色'}
    </button>
  );
}

function DevAutoLogin({ onLogin }: { onLogin: (user: User) => void }) {
  const [error, setError] = useState('');

  useEffect(() => {
    api.post<User>('/auth/dev', { userId: 'sales-demo' })
      .then(onLogin)
      .catch((reason: Error) => setError(reason.message));
  }, [onLogin]);

  return (
    <div className="boot-screen">
      <span className="brand-mark">测</span>
      <p>{error || '正在进入测试报价工作台…'}</p>
    </div>
  );
}

function DevLoginScreen({ onLogin }: { onLogin: (user: User) => void }) {
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<User[]>('/auth/dev-users').then(setUsers).catch((reason: Error) => setError(reason.message));
  }, []);

  async function choose(userId: string) {
    try {
      onLogin(await api.post<User>('/auth/dev', { userId }));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '测试登录失败');
    }
  }

  return (
    <main className="login-page">
      <section className="login-card">
        <div className="brand-mark">测</div>
        <p className="login-product">本地开发环境</p>
        <h1>选择测试角色</h1>
        <p className="login-lead">此入口只在开发模式可用，生产环境会关闭。</p>
        {error && <div className="alert error">{error}</div>}
        <div className="demo-users">
          {users.map((user) => (
            <button className="demo-user" key={user.id} onClick={() => void choose(user.id)}>
              <span className="avatar">{user.name.slice(0, 1)}</span>
              <span><strong>{user.name}</strong><small>{roleLabel(user)}</small></span>
              <span className="arrow">→</span>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}

function HomePage({
  user,
  onLogin,
  onSubmit,
}: {
  user?: User;
  onLogin: () => void;
  onSubmit: (text: string) => void;
}) {
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);
  const loggedIn = Boolean(user);

  function submit(event: FormEvent) {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    setSaved(true);
    onSubmit(value);
  }

  return (
    <main className="simple-home dark-home home-redesign">
      <ThemeToggle />
      <h1 className="site-title"><span>大地机电</span><span>报价员</span></h1>
      <div className="home-login-panel compact home-login-corner">
        <img className="wecom-logo" src="/images/wecom-logo.svg" alt="企业微信" />
        <div className="home-login-corner-copy">
          <strong>企业微信员工登录</strong>
          <small>仅限大地机电已授权员工</small>
        </div>
        <a
          className="button primary home-login-corner-button"
          href={loggedIn ? '/workbench' : '/api/auth/wecom/start'}
          onClick={(event) => {
            if (loggedIn) {
              event.preventDefault();
              onLogin();
            }
          }}
        >
          {loggedIn ? '进入工作台' : '扫码登录'}
        </a>
      </div>
      <section className="home-stack">
        <QuickQuotePanel
          user={user}
          locked={!loggedIn}
          onUseInQuote={(price, quantity) => {
            const brand = price.product.brand ?? price.product.vendorName ?? '';
            onSubmit(`${brand}${price.displayModel} ${quantity}台`);
          }}
        />
        <div className="home-copy home-requirement-card">
          <h2>报价要求</h2>
          <form className="home-requirement" onSubmit={submit}>
            <textarea
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setSaved(false);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  submit(event as unknown as FormEvent);
                }
              }}
              placeholder="例如：常州ABC公司需要AC-75VSD空压机2台，付款方式：30%预付款，发货前付清"
              aria-label="报价需求"
            />
            <button type="submit" disabled={!text.trim()} aria-label="提交需求">→</button>
          </form>
          <p className="home-example">客户名称、设备型号和数量可以写在同一条消息里，按回车即可提交。</p>
          {saved && !loggedIn && <p className="home-save-note">需求已暂存，请先完成右上角企业微信员工登录。</p>}
        </div>
      </section>
    </main>
  );
}

function LoginScreen() {
  const [loginNotice, setLoginNotice] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loginStatus = params.get('login');
    if (loginStatus === 'wecom_unconfigured') {
      setLoginNotice('企业微信登录尚未配置，请联系系统管理员。');
    } else if (loginStatus === 'wecom_error') {
      setLoginNotice(`登录未通过：${params.get('message') ?? '请联系系统管理员。'}`);
    }
    if (loginStatus) window.history.replaceState({}, '', window.location.pathname);
  }, []);

  return (
    <main className="login-page dark-login-page">
      <ThemeToggle />
      <h1 className="site-title"><span>大地机电</span><span>报价员</span></h1>
      <section className="login-card">
        <img className="wecom-logo" src="/images/wecom-logo.svg" alt="企业微信" />
        <h2>企业微信员工登录</h2>
        <p className="login-lead">仅限大地机电已授权员工使用。其他企业或个人账号无法登录，也不能生成报价。</p>
        {loginNotice && <div className="alert warning">{loginNotice}</div>}
        <a className="button primary full login-wecom-button" href="/api/auth/wecom/start">企业微信扫码登录</a>
        <p className="login-note">登录成功后自动进入报价工作台</p>
      </section>
    </main>
  );
}

function AppShell({
  user,
  view,
  onView,
  onHome,
  onLogout,
  children,
}: {
  user: User;
  view: View;
  onView: (view: View) => void;
  onHome: () => void;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  const navItems: Array<{ id: View; label: string; icon: string; roles?: User['role'][] }> = [
    { id: 'dashboard', label: '工作台', icon: '▦' },
    { id: 'workspace', label: '新建报价', icon: '＋', roles: ['sales', 'pricing_admin', 'system_admin'] },
    { id: 'quotes', label: '报价中心', icon: '▤' },
    { id: 'approvals', label: '审批中心', icon: '✓', roles: ['pricing_admin', 'boss', 'system_admin'] },
    { id: 'catalog', label: '价格库', icon: '价', roles: ['pricing_admin', 'boss', 'system_admin'] },
    { id: 'audit', label: '审计记录', icon: '◎', roles: ['boss', 'system_admin'] },
  ];
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand"><span className="brand-mark small">大</span><div><strong>大地机电AI报价员</strong><small>设备与服务报价</small></div></div>
        <nav>
          <button onClick={onHome}><span>⌂</span>首页</button>
          {navItems.filter((item) => !item.roles || item.roles.some((role) => hasRole(user, role))).map((item) => (
            <button key={item.id} className={view === item.id ? 'active' : ''} onClick={() => onView(item.id)}>
              <span>{item.icon}</span>{item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-user">
          <span className="avatar dark">{user.name.slice(0, 1)}</span>
          <div><strong>{user.name}</strong><small>{roleLabel(user)}</small></div>
          <button title="退出" onClick={onLogout}>↪</button>
        </div>
      </aside>
      <section className="main-column">
        <header className="topbar">
          <div className="topbar-context">
            <span>大地机电AI报价员</span>
            <strong>{navItems.find((item) => item.id === view)?.label ?? '工作台'}</strong>
          </div>
          <div className="connection"><span className={online ? 'online-dot' : 'offline-dot'} />{online ? '在线 · 价格实时同步' : '离线 · 使用最近价格快照'}</div>
          <div className="topbar-note">正式报价必须经过人工审批，AI 不自动外发</div>
        </header>
        <main className="content">{children}</main>
      </section>
    </div>
  );
}

function Dashboard({ user, onNew }: { user: User; onNew: () => void }) {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [catalog, setCatalog] = useState<PriceCatalogVersion>();
  const [approvals, setApprovals] = useState<Array<QuoteApproval & { quoteNo?: string }>>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get<Quote[]>('/quotes'),
      api.get<PriceCatalogVersion>('/catalog'),
      hasRole(user, 'sales') && getUserRoles(user).length === 1 ? Promise.resolve([]) : api.get<Array<QuoteApproval & { quoteNo?: string }>>('/approvals'),
    ]).then(([quoteRows, catalogRow, approvalRows]) => {
      setQuotes(quoteRows);
      setCatalog(catalogRow);
      setApprovals(approvalRows);
      void cachePublishedCatalog(catalogRow);
    }).catch(async (reason: Error) => {
      setError(reason.message);
      const cached = await getCachedCatalog();
      if (cached) setCatalog(cached);
    });
  }, [user.role, user.roles?.join(',')]);

  const pending = quotes.filter((quote) => ['pending_pricing', 'pending_boss'].includes(quote.status)).length;
  const published = quotes.filter((quote) => quote.status === 'published').length;
  return (
    <>
      <div className="page-heading">
        <div><p className="eyebrow">{roleLabel(user)}工作台</p><h1>上午好，{user.name}</h1><p>今天继续把复杂的报价，变成清晰可追溯的决策。</p></div>
        <button className="button primary" onClick={onNew}>＋ 新建报价</button>
      </div>
      {error && <div className="alert warning">{error}</div>}
      <section className="metric-grid">
        <article className="metric-card accent"><span>当前价格版本</span><strong>{catalog ? `V${catalog.version}` : '—'}</strong><small>{catalog?.label ?? '价格库尚未同步'}</small></article>
        <article className="metric-card"><span>待处理报价</span><strong>{pending}</strong><small>需要复核或审批</small></article>
        <article className="metric-card"><span>已发布报价</span><strong>{published}</strong><small>版本永久保留</small></article>
        <article className="metric-card"><span>我的审批任务</span><strong>{approvals.length}</strong><small>{getUserRoles(user).length === 1 && hasRole(user, 'sales') ? '销售账号无审批权限' : '按角色精确分配'}</small></article>
      </section>
      <section className="dashboard-grid">
        <article className="panel">
          <div className="panel-heading"><div><p className="eyebrow">最近报价</p><h2>业务动态</h2></div><span>{quotes.length} 条</span></div>
          <div className="quote-list compact">
            {quotes.slice(0, 5).map((quote) => (
              <div className="quote-row" key={quote.id}>
                <div className="quote-main"><strong>{quote.customerName ?? '未填写客户'}</strong><small>{quote.quoteNo} · V{quote.version}</small></div>
                <div className="quote-money">{formatMoney(quote.factoryTotalMinor ?? quote.totalMinor)}</div>
                <span className={`status ${quote.status}`}>{STATUS_LABELS[quote.status]}</span>
              </div>
            ))}
            {quotes.length === 0 && <div className="empty">还没有报价，先创建一个需求草稿。</div>}
          </div>
        </article>
        <article className="panel trust-panel">
          <p className="eyebrow">核价原则</p>
          <h2>AI 负责理解，规则负责算钱。</h2>
          <ul className="check-list">
            <li><span>1</span>完整价目表不发送给模型</li>
            <li><span>2</span>每个金额都带价格版本和公式</li>
            <li><span>3</span>未知型号与超限折扣转人工</li>
            <li><span>4</span>正式版本只能由人工批准</li>
          </ul>
        </article>
      </section>
    </>
  );
}

function RequirementPanel({ requirement }: { requirement: RequirementDraft }) {
  return (
    <div className="requirement-summary">
      <div className="panel-heading"><div><p className="eyebrow">结构化需求</p><h2>{requirement.title}</h2></div><span className="confidence">置信度 {Math.round(requirement.confidence * 100)}%</span></div>
      <div className="requirement-items">
        {requirement.items.map((item) => (
          <div className="requirement-item" key={item.id}>
            <div><strong>{item.productName ?? item.rawText}</strong><small>{item.unit ?? '项'} · {item.rawText}{item.quotedUnitPriceMinor !== undefined ? ` · 特殊单价 ${formatMoney(item.quotedUnitPriceMinor)}` : ''}</small></div>
            <span className="quantity">× {item.quantity}</span>
          </div>
        ))}
        {requirement.items.length === 0 && <div className="empty">尚未识别出设备型号</div>}
      </div>
      <div className="tag-row">
        {requirement.services.installationRequired && <span className="tag">需要安装</span>}
        {requirement.services.commissioningRequired && <span className="tag">需要调试</span>}
        {requirement.services.distanceKm !== undefined && <span className="tag">{requirement.services.distanceKm} km</span>}
        {requirement.services.specialRequirements && <span className="tag warn">含非标要求</span>}
      </div>
      {requirement.specialOptions && requirement.specialOptions.length > 0 && (
        <div className="recognized-options">
          <strong>特殊选配（机床内部部件）</strong>
          {requirement.specialOptions.map((option) => {
            const item = typeof option === 'string' ? { name: option, quantity: 1, unit: '', priceMinor: undefined } : option;
            return <p key={`special-${item.name}-${item.quantity}`}>{item.name} × {item.quantity}{item.unit ?? ''}{item.priceMinor !== undefined ? `，${formatMoney(item.priceMinor)}/${item.unit ?? '套'}` : ''}</p>;
          })}
        </div>
      )}
      {requirement.selectedOptions && requirement.selectedOptions.length > 0 && (
        <div className="recognized-options">
          <strong>外部协作选配</strong>
          {requirement.selectedOptions.map((option) => {
            const item = typeof option === 'string' ? { name: option, quantity: 1, unit: '', priceMinor: undefined } : option;
            return <p key={`external-${item.name}-${item.quantity}`}>{item.name} × {item.quantity}{item.unit ?? ''}{item.priceMinor !== undefined ? `，${formatMoney(item.priceMinor)}/${item.unit ?? '套'}` : ''}</p>;
          })}
        </div>
      )}
      {requirement.paymentTerms && <div className="recognized-payment"><strong>付款方式</strong><p>{requirement.paymentTerms}</p></div>}
      {requirement.clarificationQuestions.length > 0 && (
        <div className="question-box"><strong>待补充信息</strong>{requirement.clarificationQuestions.map((question) => <p key={question}>• {question}</p>)}</div>
      )}
    </div>
  );
}

function QuotePreview({ quote, onQuoteChange, user }: { quote: Quote; onQuoteChange: (quote: Quote) => void; user: User }) {
  const [saving, setSaving] = useState('');
  const showCostTrace = hasRole(user, 'pricing_admin') || hasRole(user, 'system_admin');
  async function changeQuantity(lineId: string, quantity: number) {
    if (quantity <= 0 || quote.status !== 'draft') return;
    setSaving(lineId);
    try {
      onQuoteChange(await api.patch<Quote>(`/quotes/${quote.id}/lines/${lineId}`, { quantity }));
    } finally {
      setSaving('');
    }
  }
  return (
    <div className="quote-preview">
      <div className="panel-heading"><div><p className="eyebrow">核价明细 · 出厂价</p><h2>{quote.quoteNo}</h2></div><span className={`status ${quote.status}`}>{STATUS_LABELS[quote.status]}</span></div>
      <div className="line-table">
        {quote.lines.map((line) => (
          <div className="quote-line" key={line.id}>
            <div className="line-main">
              <strong>{line.name}</strong>
              <small>{line.specification}</small>
              {line.kind === 'manual' && <small>外部协作设备</small>}
              {line.specialOptions && line.specialOptions.length > 0 && (
                <small>特殊选配：{line.specialOptions.map((option) => typeof option === 'string' ? option : option.name).join('、')}</small>
              )}
              <small className="trace">{showCostTrace ? line.ruleTrace[0] : '按当前发布价格版本完成核价'}</small>
            </div>
            <label className="qty-input">× <input disabled={quote.status !== 'draft' || saving === line.id} value={line.quantity} min="0.01" step="0.5" type="number" onChange={(event) => void changeQuantity(line.id, Number(event.target.value))} /></label>
            <div className="line-price">
              <strong>{formatMoney(line.factoryTotalAmountMinor ?? line.totalAmountMinor)}</strong>
              <small>出厂价</small>
            </div>
            {line.status !== 'calculated' && <span className="line-warning">{line.status === 'needs_price' ? '待补价' : '待复核'}</span>}
          </div>
        ))}
      </div>
      <div className="totals">
        <div className="grand-total"><span>出厂价合计（含税）</span><strong>{formatMoney(quote.factoryTotalMinor ?? quote.totalMinor)}</strong></div>
      </div>
      {quote.approvals.length > 0 && (
        <div className="approval-history">
          <strong>审批记录</strong>
          {quote.approvals.map((approval) => <p key={approval.id}>{approval.requiredRole === 'boss' ? '老板' : '报价管理员'} · {approval.status === 'pending' ? '待处理' : approval.status === 'approved' ? '已同意' : '已驳回'}：{approval.reason}</p>)}
        </div>
      )}
    </div>
  );
}

function QuickQuotePanel({
  user,
  onUseInQuote,
  locked = false,
}: {
  user?: User;
  onUseInQuote?: (price: QuickQuotePrice, quantity: number) => void;
  locked?: boolean;
}) {
  const [catalog, setCatalog] = useState<PriceCatalogVersion>();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<QuickQuoteResult>();
  const [selectedProductId, setSelectedProductId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    if (locked) {
      setLoading(false);
      return () => { active = false; };
    }
    async function loadCatalog() {
      try {
        const published = await api.get<PriceCatalogVersion>('/catalog');
        if (!active) return;
        setCatalog(published);
        await cachePublishedCatalog(published);
      } catch {
        const cached = await getCachedCatalog();
        if (!active) return;
        if (cached) setCatalog(cached);
        else setError('价格库暂时无法加载，请联网后重试。');
      } finally {
        if (active) setLoading(false);
      }
    }
    void loadCatalog();
    return () => { active = false; };
  }, [locked]);

  function search(event: FormEvent) {
    event.preventDefault();
    if (locked) {
      setError('请先通过右上角企业微信员工登录，再查询价格。');
      return;
    }
    if (!catalog || !query.trim()) return;
    const next = buildQuickQuote(catalog, query);
    if (!next) {
      setResult(undefined);
      setError('没有找到对应型号，请检查品牌和型号是否完整。');
      return;
    }
    setError('');
    setResult(next);
    setSelectedProductId(next.selected.product.id);
  }

  const selected = result?.related.find((row) => row.product.id === selectedProductId) ?? result?.selected;
  const showCost = Boolean(user && (hasRole(user, 'boss') || hasRole(user, 'system_admin')));
  return (
    <section className="quick-quote-panel">
      <div className="quick-quote-heading">
        <div>
          <p className="eyebrow">快速询价</p>
          <h2>输入型号，立即得到可回复价格</h2>
          <p>默认按 1 台计算；可以输入“金火TT5050D 2台”。只查询已发布价格，不生成正式文件。</p>
        </div>
        <span className="quick-quote-version">{locked ? '登录后查价' : catalog ? `价格库 V${catalog.version}` : loading ? '加载价格库…' : '价格库未连接'}</span>
      </div>
      <form className="quick-quote-form" onSubmit={search}>
        <input
          aria-label="快速询价型号"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setError('');
          }}
          placeholder="例如：金火TT5050D 2台"
        />
        <button className="button primary" disabled={locked || loading || !catalog || !query.trim()}>{locked ? '请先登录' : loading ? '加载中…' : '立即查价'}</button>
      </form>
      {error && <div className="alert error quick-quote-alert">{error}</div>}
      {result && selected && (
        <div className="quick-quote-result">
          <div className="quick-quote-featured">
            <div className="quick-quote-featured-main">
              <span className="quick-quote-kicker">客户回复价 · 出厂价</span>
              <strong>{formatMoney(selected.factoryUnitPriceMinor)}</strong>
              <p>{selected.product.brand ?? selected.product.vendorName ?? ''} {selected.displayModel} · {result.quantity} 台</p>
              {result.quantity > 1 && <small>单台 {formatMoney(selected.factoryUnitPriceMinor)}，合计 {formatMoney(selected.factoryUnitPriceMinor * result.quantity)}</small>}
            </div>
            <div className="quick-quote-featured-side">
              <span>市场指导价</span>
              <strong>{formatMoney(selected.marketGuideUnitPriceMinor)}</strong>
              {!locked && onUseInQuote && <button type="button" className="button dark" onClick={() => onUseInQuote(selected, result.quantity)}>带入正式报价</button>}
            </div>
          </div>
          <div className="quick-quote-config">
            <span>当前配置</span>
            <p>{selected.configurationText || selected.product.specification || '标准配置'}</p>
          </div>
          {showCost && (
            <div className="quick-quote-cost">
              <div><span>厂商表价</span><strong>{selected.vendorTablePriceMinor === undefined ? '—' : formatMoney(selected.vendorTablePriceMinor)}</strong></div>
              <div><span>进价下浮</span><strong>{selected.costDiscountBps === undefined ? '—' : `${selected.costDiscountBps / 100}%`}</strong></div>
              <div><span>真实进价</span><strong>{selected.realCostUnitPriceMinor === undefined ? '—' : formatMoney(selected.realCostUnitPriceMinor)}</strong></div>
              <div><span>出厂加价</span><strong>{selected.factoryMarkupBps === undefined ? '—' : `${selected.factoryMarkupBps / 100}%`}</strong></div>
              <div><span>指导价加价</span><strong>{selected.marketGuideMarkupBps === undefined ? '—' : `${selected.marketGuideMarkupBps / 100}%`}</strong></div>
            </div>
          )}
          <div className="quick-quote-variants">
            <div className="quick-quote-variants-head"><strong>相关型号与配置</strong><span>{result.related.length} 个价格</span></div>
            <div className="quick-quote-variant-list">
              {result.related.map((row) => (
                <button
                  type="button"
                  key={row.product.id}
                  className={row.product.id === selected.product.id ? 'quick-quote-variant active' : 'quick-quote-variant'}
                  onClick={() => setSelectedProductId(row.product.id)}
                >
                  <span className="quick-quote-variant-main">
                    <strong>{row.product.name}</strong>
                    <small>{row.configurationText || row.product.specification || '标准配置'}</small>
                  </span>
                  <span className="quick-quote-variant-price">
                    <strong>{formatMoney(row.factoryUnitPriceMinor)}</strong>
                    <small>出厂价</small>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function QuoteWorkspace({ user }: { user: User }) {
  const rawHomeDraft = sessionStorage.getItem('quotation.homeDraft') ?? '';
  let initialText = rawHomeDraft;
  try {
    const parsed = rawHomeDraft ? JSON.parse(rawHomeDraft) as { customerName?: string; items?: Array<{ model: string; quantity: number }> } : undefined;
    if (parsed?.items) {
      initialText = [parsed.customerName, ...parsed.items.map((item) => `${item.model} ${item.quantity}台`)].filter(Boolean).join('，');
    }
  } catch {
    initialText = rawHomeDraft;
  }
  const [text, setText] = useState(initialText);
  const [customerName, setCustomerName] = useState('');
  const [requirement, setRequirement] = useState<RequirementDraft>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [quote, setQuote] = useState<Quote>();
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => {
    sessionStorage.removeItem('quotation.homeDraft');
    getOfflineDrafts().then((drafts) => {
      if (drafts[0] && !requirement) setRequirement(drafts[0]);
    });
  }, []);

  async function uploadFiles(): Promise<string[]> {
    const ids: string[] = [];
    for (const file of files) {
      const attachment = await api.upload<UploadedAttachment>('/requirements/attachments', file);
      if (attachment.warning) setNotice(attachment.warning);
      ids.push(attachment.id);
    }
    return ids;
  }

  async function createRequirement(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) return;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const attachmentIds = await uploadFiles();
      const response = await api.post<RequirementResponse>('/requirements', {
        text: text.trim(),
        attachmentIds,
        customerName: customerName.trim() || undefined,
      });
      setRequirement(response.requirement);
      setMessages(response.messages);
      setFiles([]);
      if (response.warning) setNotice(response.warning);
      await cacheOfflineDraft(response.requirement);
    } catch (reason) {
      const fallback = await getCachedCatalog();
      if (requirement && fallback) {
        const offline = await calculateOfflineQuote(requirement);
        setNotice(offline.warning ?? '网络不可用，需求已暂存。');
        await cacheOfflineDraft(requirement);
      } else {
        setError(reason instanceof Error ? reason.message : '需求提交失败');
      }
    } finally {
      setLoading(false);
    }
  }

  async function addMessage(event: FormEvent) {
    event.preventDefault();
    if (!requirement || !text.trim()) return;
    setLoading(true);
    setError('');
    try {
      const attachmentIds = files.length > 0 ? await uploadFiles() : [];
      const response = await api.post<RequirementResponse>(`/requirements/${requirement.id}/messages`, { text: text.trim(), attachmentIds });
      setRequirement(response.requirement);
      setMessages(response.messages);
      setText('');
      setFiles([]);
      if (response.warning) setNotice(response.warning);
      await cacheOfflineDraft(response.requirement);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '消息发送失败');
      await cacheOfflineDraft(requirement);
    } finally {
      setLoading(false);
    }
  }

  async function createQuote(): Promise<Quote | undefined> {
    if (!requirement) return undefined;
    setLoading(true);
    setError('');
    try {
      const created = await api.post<Quote>('/quotes', {
        requirementId: requirement.id,
        quoteDiscountBps: 0,
      });
      setQuote(created);
      setNotice(created.status === 'approved'
        ? '核价完成，报价已直接通过，可以预览或下载PDF。'
        : '核价完成。确认明细后提交审批；系统不会自动发送给客户。');
      return created;
    } catch (reason) {
      const offline = await calculateOfflineQuote(requirement);
      if (offline.lines.length > 0) {
        setNotice(`${offline.warning} 离线核价合计：${formatMoney(offline.totalMinor)}`);
      } else {
        setError(reason instanceof Error ? reason.message : '核价失败');
      }
      return undefined;
    } finally {
      setLoading(false);
    }
  }

  async function createQuoteAndDownloadPdf() {
    let created = quote ?? await createQuote();
    if (!created) return;
    if (created.status === 'draft') {
      created = await api.post<Quote>(`/quotes/${created.id}/submit`);
      setQuote(created);
    }
    if (created.status !== 'approved' && created.status !== 'published') {
      setError(created.status === 'pending_boss' ? '报价需要老板审批后才能生成客户版 PDF。' : '报价需要报价管理员补全后才能生成客户版 PDF。');
      return;
    }
    const preflight = await api.get<{ ready: boolean; issues: string[]; pageCount: number }>(`/quotes/${created.id}/preflight`);
    if (!preflight.ready) {
      setError(`暂时不能生成正式 PDF：${preflight.issues.join('；')}`);
      return;
    }
    setNotice(`完整性校验通过，正在生成 ${preflight.pageCount} 页报价单。`);
    try {
      const filename = await download(`/quotes/${created.id}/export/pdf`);
      setNotice(`已下载 ${filename}，请到浏览器的“下载”文件夹查看。`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'PDF 下载失败');
    }
  }

  async function submitQuote() {
    if (!quote) return;
    setLoading(true);
    try {
      const next = await api.post<Quote>(`/quotes/${quote.id}/submit`);
      setQuote(next);
      setNotice(next.status === 'approved' ? '无异常项，报价已通过内部确认。' : `已提交给${next.status === 'pending_boss' ? '老板' : '报价管理员'}审批。`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '提交失败');
    } finally {
      setLoading(false);
    }
  }

  function useQuickQuote(price: QuickQuotePrice, quantity: number) {
    const model = price.displayModel;
    const brand = price.product.brand ?? price.product.vendorName ?? '';
    setRequirement(undefined);
    setMessages([]);
    setQuote(undefined);
    setCustomerName('');
    setFiles([]);
    setError('');
    setNotice(`已将 ${brand}${model} × ${quantity} 带入正式报价，请补充客户名称和付款方式。`);
    setText(`${brand}${model} ${quantity}台`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <>
      <QuickQuotePanel user={user} onUseInQuote={useQuickQuote} />
      <div className="workspace">
      <section className="chat-panel">
        <div className="panel-heading"><div><p className="eyebrow">需求采集</p><h2>与大地机电AI报价员沟通</h2></div><span className="engine-badge">规则兜底开启</span></div>
        <div className="chat-stream">
          {messages.length === 0 && (
            <div className="assistant-intro">
              <span className="ai-avatar">AI</span>
              <div><strong>告诉我设备型号、数量、交付和安装要求。</strong><p>可以同时上传 Excel 清单、PDF 或图片。我会先整理缺口，再交给规则引擎核价。</p></div>
            </div>
          )}
          {messages.map((message) => (
            <div className={`message ${message.role}`} key={message.id}>
              <span className="bubble">{message.content.split('\n').map((line) => <span key={line}>{line}<br /></span>)}</span>
              <small>{formatDate(message.createdAt)}</small>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <form className="composer" onSubmit={messages.length === 0 ? createRequirement : addMessage}>
          <div className="customer-input"><input aria-label="客户名称" value={customerName} onChange={(event) => setCustomerName(event.target.value)} placeholder="客户名称" disabled={Boolean(requirement)} /></div>
          <textarea value={text} onChange={(event) => setText(event.target.value)} placeholder="例如：客户需要 2 台 75kW 空压机，包含安装调试，距离 35 公里……" rows={4} />
          <div className="composer-actions">
            <label className="attach-button">附件<input type="file" multiple accept=".xlsx,.xlsm,.pdf,.docx,.csv,.txt,image/*" onChange={(event) => setFiles(Array.from(event.target.files ?? []))} /></label>
            {files.length > 0 && <span className="file-count">{files.length} 个文件</span>}
            <button className="button primary" disabled={loading || !text.trim()}>{loading ? '处理中…' : messages.length === 0 ? '开始识别' : '继续补充'}</button>
          </div>
        </form>
      </section>
      <section className="result-panel">
        {notice && <div className="alert info">{notice}</div>}
        {error && <div className="alert error">{error}</div>}
        {requirement ? <RequirementPanel requirement={requirement} /> : <div className="result-placeholder"><span>01</span><h2>先提交需求</h2><p>系统会在右侧生成结构化设备与服务清单。</p></div>}
        {quote ? <QuotePreview quote={quote} onQuoteChange={setQuote} user={user} /> : requirement && (
          <div className="quote-level-area">
            <button className="button primary wide" onClick={() => void createQuoteAndDownloadPdf()} disabled={loading}>生成并下载双价格 PDF 报价单</button>
            <button className="button ghost wide" onClick={() => void createQuote()} disabled={loading}>仅生成核价草稿</button>
          </div>
        )}
        {quote && (
          <div className="quote-actions">
            <button className="button ghost" onClick={() => previewPdf(`/quotes/${quote.id}/export/pdf?inline=1`)}>预览 PDF</button>
            <button className="button primary" onClick={() => void createQuoteAndDownloadPdf()}>下载 PDF 报价单</button>
            <button className="button ghost" onClick={() => void download(`/quotes/${quote.id}/export/xlsx`).then((filename) => setNotice(`已下载 ${filename}，请到浏览器的“下载”文件夹查看。`)).catch((reason: Error) => setError(reason.message))}>导出 Excel</button>
            {quote.status === 'draft' && <button className="button dark" onClick={() => void submitQuote()} disabled={loading}>提交人工审批</button>}
          </div>
        )}
      </section>
      </div>
    </>
  );
}


function QuoteCenter({ user }: { user: User }) {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [selected, setSelected] = useState<Quote>();
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showOptionForm, setShowOptionForm] = useState(false);
  const [optionForm, setOptionForm] = useState({ name: '', quantity: '1', unit: '套', unitPrice: '', note: '' });
  async function load() {
    try { setQuotes(await api.get<Quote[]>('/quotes')); } catch (reason) { setError(reason instanceof Error ? reason.message : '加载失败'); }
  }
  useEffect(() => { void load(); }, []);
  async function select(quote: Quote) {
    const detail = await api.get<QuoteDetail>(`/quotes/${quote.id}`);
    setSelected(detail.quote);
    setAudit(detail.audit);
  }
  async function addOption() {
    if (!selected) return;
    const quantity = Number(optionForm.quantity);
    const unitPriceMinor = Math.round(Number(optionForm.unitPrice) * 100);
    if (!optionForm.name.trim() || !Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(unitPriceMinor) || unitPriceMinor <= 0) {
      setError('请填写选配名称、数量和有效的特殊价格。');
      return;
    }
    try {
      const next = await api.post<Quote>(`/quotes/${selected.id}/options`, {
        name: optionForm.name.trim(),
        quantity,
        unit: optionForm.unit.trim() || '套',
        unitPriceMinor,
        note: optionForm.note.trim() || undefined,
      });
      setSelected(next);
      setShowOptionForm(false);
      setOptionForm({ name: '', quantity: '1', unit: '套', unitPrice: '', note: '' });
      setNotice(`已增加选配，并自动生成 V${next.version} 修订版。`);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '增加选配失败');
    }
  }

  async function removeSelected() {
    if (!selected) return;
    if (!window.confirm(`确定删除报价 ${selected.quoteNo}？此操作不会影响其他报价。`)) return;
    try {
      await api.delete(`/quotes/${selected.id}`);
      setNotice(`报价 ${selected.quoteNo} 已删除。`);
      setSelected(undefined);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '删除失败');
    }
  }

  async function action(path: string, body?: unknown) {
    if (!selected) return;
    try {
      const next = body ? await api.post<Quote>(path, body) : await api.post<Quote>(path);
      setSelected(next);
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : '操作失败'); }
  }
  return (
    <div className="split-page quote-center-layout">
      <section className="page-heading full-span"><div><p className="eyebrow">版本与审批记录</p><h1>报价中心</h1><p>销售看到自己的报价，管理员和老板可按权限查看全量版本。</p></div></section>
      {notice && <div className="alert info full-span">{notice}</div>}
      {error && <div className="alert error full-span">{error}</div>}
      <section className="panel list-panel">
        <div className="panel-heading"><h2>全部报价</h2><span>{quotes.length} 条</span></div>
        <div className="quote-list selectable">
          {quotes.map((quote) => (
            <button key={quote.id} className={selected?.id === quote.id ? 'quote-row active' : 'quote-row'} onClick={() => void select(quote)}>
              <span className="quote-main"><strong>{quote.customerName ?? '未填写客户'}</strong><small>{quote.quoteNo} · {formatDate(quote.updatedAt)}</small></span>
              <span className="quote-money">{formatMoney(quote.factoryTotalMinor ?? quote.totalMinor)}</span>
              <span className={`status ${quote.status}`}>{STATUS_LABELS[quote.status]}</span>
            </button>
          ))}
          {quotes.length === 0 && <div className="empty">暂无报价记录</div>}
        </div>
      </section>
      <section className="panel detail-panel">
        {!selected ? <div className="empty tall">选择一条报价查看明细</div> : (
          <>
            <div className="panel-heading"><div><p className="eyebrow">{selected.quoteNo}</p><h2>{selected.customerName ?? '未填写客户'}</h2></div><span className={`status ${selected.status}`}>{STATUS_LABELS[selected.status]}</span></div>
            <QuotePreview quote={selected} onQuoteChange={setSelected} user={user} />
            <div className="quote-actions detail-actions">
              <button className="button ghost" onClick={() => void download(`/quotes/${selected.id}/export/xlsx`).then((filename) => setNotice(`已下载 ${filename}，请到浏览器的“下载”文件夹查看。`)).catch((reason: Error) => setError(reason.message))}>Excel</button>
              <button className="button ghost" onClick={() => previewPdf(`/quotes/${selected.id}/export/pdf?inline=1`)}>预览 PDF</button>
              <button className="button ghost" onClick={() => void download(`/quotes/${selected.id}/export/pdf`).then((filename) => setNotice(`已下载 ${filename}，请到浏览器的“下载”文件夹查看。`)).catch((reason: Error) => setError(reason.message))}>下载 PDF</button>
              <button className="button dark" onClick={() => setShowOptionForm((current) => !current)}>新增客户选配</button>
              {['published', 'approved', 'rejected', 'superseded'].includes(selected.status) && <button className="button ghost" onClick={() => void action(`/quotes/${selected.id}/revisions`)}>创建修订版</button>}
              {((hasRole(user, 'sales') && selected.createdBy === user.id) || hasRole(user, 'system_admin')) && <button className="button danger" onClick={() => void removeSelected()}>删除报价</button>}
            </div>
            {showOptionForm && (
              <section className="panel option-form-panel">
                <div className="panel-heading option-form-heading">
                  <div>
                    <p className="eyebrow">报价修订</p>
                    <h2>新增客户选配</h2>
                    <small>保存后自动创建修订版，不覆盖原报价。</small>
                  </div>
                </div>
                <div className="option-form-grid">
                  <label className="option-field option-field-name"><span>选配名称</span><input value={optionForm.name} onChange={(event) => setOptionForm((current) => ({ ...current, name: event.target.value }))} placeholder="例如：机械臂" /></label>
                  <label className="option-field"><span>数量</span><input type="number" min="0.01" value={optionForm.quantity} onChange={(event) => setOptionForm((current) => ({ ...current, quantity: event.target.value }))} /></label>
                  <label className="option-field"><span>单位</span><input value={optionForm.unit} onChange={(event) => setOptionForm((current) => ({ ...current, unit: event.target.value }))} /></label>
                  <label className="option-field option-field-price"><span>单价（元）</span><input type="number" min="0.01" step="0.01" value={optionForm.unitPrice} onChange={(event) => setOptionForm((current) => ({ ...current, unitPrice: event.target.value }))} /></label>
                </div>
                <label className="option-field option-field-note"><span>备注</span><input value={optionForm.note} onChange={(event) => setOptionForm((current) => ({ ...current, note: event.target.value }))} placeholder="选填" /></label>
                <div className="quote-actions option-form-actions"><button type="button" className="button primary" onClick={() => void addOption()}>保存选配并生成修订版</button><button type="button" className="button ghost" onClick={() => setShowOptionForm(false)}>取消</button></div>
              </section>
            )}
            {audit.length > 0 && <div className="audit-mini"><strong>操作轨迹</strong>{audit.slice(0, 8).map((event) => <p key={event.id}><span>{formatDate(event.createdAt)}</span><strong>{event.actorName ?? event.actorId}</strong><span>{auditLabel(event.action)}</span></p>)}</div>}
          </>
        )}
      </section>
    </div>
  );
}

function Approvals({ user }: { user: User }) {
  const [items, setItems] = useState<Array<QuoteApproval & { quoteNo?: string }>>([]);
  const [quotes, setQuotes] = useState<Record<string, Quote>>({});
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  async function load() {
    try {
      const rows = await api.get<Array<QuoteApproval & { quoteNo?: string }>>('/approvals');
      setItems(rows);
      const quoteRows = await api.get<Quote[]>('/quotes');
      setQuotes(Object.fromEntries(quoteRows.map((quote) => [quote.id, quote])));
    } catch (reason) { setError(reason instanceof Error ? reason.message : '加载失败'); }
  }
  useEffect(() => { void load(); }, []);
  async function decide(approval: QuoteApproval, decision: 'approved' | 'rejected') {
    try {
      await api.post<Quote>(`/quotes/${approval.quoteId}/decision`, { decision, note });
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : '审批失败'); }
  }
  return (
    <>
      <div className="page-heading"><div><p className="eyebrow">{roleLabel(user)}权限</p><h1>审批中心</h1><p>处理缺价、非标、超限折扣和低置信度核价项。</p></div></div>
      {error && <div className="alert error">{error}</div>}
      <section className="approval-grid">
        {items.map((approval) => {
          const quote = quotes[approval.quoteId];
          return (
            <article className="approval-card" key={approval.id}>
              <div className="approval-head"><span className="approval-type">{approval.requiredRole === 'boss' ? '价格决策' : '核价复核'}</span><span>{formatDate(approval.requestedAt)}</span></div>
              <h2>{quote?.customerName ?? approval.quoteNo ?? '报价审批'}</h2>
              <p className="approval-reason">{approval.reason}</p>
              {quote && <div className="approval-summary"><span>{quote.lines.length} 个明细项</span><strong>{formatMoney(quote.factoryTotalMinor ?? quote.totalMinor)}</strong></div>}
              <textarea placeholder="审批备注（驳回时建议填写）" value={note} onChange={(event) => setNote(event.target.value)} />
              <div className="quote-actions"><button className="button primary" onClick={() => void decide(approval, 'approved')}>同意</button><button className="button danger" onClick={() => void decide(approval, 'rejected')}>驳回</button></div>
            </article>
          );
        })}
        {items.length === 0 && <div className="empty tall">当前没有需要你处理的审批。</div>}
      </section>
    </>
  );
}

interface CatalogImportPreview {
  fileName: string;
  sheetName: string;
  headerRow: number;
  mapping: Record<string, string | null>;
  warnings: string[];
  stats: { totalRows: number; validRows: number; invalidRows: number };
  catalog: Omit<PriceCatalogVersion, 'id' | 'version' | 'status'>;
}

function CatalogPage({ user }: { user: User }) {
  const [catalog, setCatalog] = useState<PriceCatalogVersion>();
  const [versions, setVersions] = useState<PriceCatalogVersion[]>([]);
  const [preview, setPreview] = useState<CatalogImportPreview>();
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  async function load() {
    try {
      const [published, rows] = await Promise.all([api.get<PriceCatalogVersion>('/catalog'), api.get<PriceCatalogVersion[]>('/catalog/versions')]);
      setCatalog(published);
      setVersions(rows);
      await cachePublishedCatalog(published);
    } catch (reason) { setError(reason instanceof Error ? reason.message : '加载失败'); }
  }
  useEffect(() => { void load(); }, []);

  async function previewImport(file: File) {
    setImporting(true);
    setError('');
    setNotice('');
    try {
      setPreview(await api.upload<CatalogImportPreview>('/catalog/import/preview', file));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Excel 预检失败');
    } finally {
      setImporting(false);
    }
  }

  async function confirmImport() {
    if (!preview) return;
    setImporting(true);
    setError('');
    try {
      const draft = await api.post<PriceCatalogVersion>('/catalog/import', preview.catalog);
      const published = await api.post<PriceCatalogVersion>(`/catalog/${draft.id}/publish`);
      setPreview(undefined);
      setNotice(`已发布价格版本 V${published.version}，新报价将引用该版本。`);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '价格版本发布失败');
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      <div className="page-heading"><div><p className="eyebrow">唯一价格主数据</p><h1>价格库</h1><p>Excel 只能导入为草稿；经确认发布后，报价才可引用该版本。</p></div><div className="heading-actions">{(hasRole(user, 'pricing_admin') || hasRole(user, 'system_admin')) && <label className="button primary">{importing ? '预检中…' : '导入 Excel 预检'}<input className="hidden-file" type="file" accept=".xlsx,.xlsm" onChange={(event) => event.target.files?.[0] && void previewImport(event.target.files[0])} /></label>}<span className="status published">已发布 V{catalog?.version ?? '—'}</span></div></div>
      {error && <div className="alert error">{error}</div>}
      {notice && <div className="alert info">{notice}</div>}
      {preview && (
        <section className="panel import-preview">
          <div className="panel-heading"><div><p className="eyebrow">导入预检，尚未发布</p><h2>{preview.fileName}</h2></div><span>{preview.sheetName} · 表头第 {preview.headerRow} 行</span></div>
          <div className="metric-grid import-metrics"><article className="metric-card"><span>有效产品</span><strong>{preview.stats.validRows}</strong></article><article className="metric-card"><span>异常/跳过</span><strong>{preview.stats.invalidRows}</strong></article><article className="metric-card"><span>识别字段</span><strong>{Object.values(preview.mapping).filter(Boolean).length}</strong></article></div>
          <div className="import-warnings">{preview.warnings.length > 0 ? preview.warnings.map((warning) => <p key={warning}>• {warning}</p>) : <p>未发现结构性问题，可以提交发布审批。</p>}</div>
          <div className="quote-actions"><button className="button dark" disabled={importing || preview.stats.validRows === 0} onClick={() => void confirmImport()}>确认并发布 V{(catalog?.version ?? 0) + 1}</button><button className="button ghost" onClick={() => setPreview(undefined)}>取消</button></div>
        </section>
      )}
      <section className="metric-grid catalog-metrics">
        <article className="metric-card"><span>产品数量</span><strong>{catalog?.products.length ?? 0}</strong><small>当前发布版本</small></article>
        <article className="metric-card"><span>服务规则</span><strong>{catalog?.serviceRules.length ?? 0}</strong><small>安装、调试、运费</small></article>
        <article className="metric-card"><span>生效日期</span><strong>{catalog ? new Date(catalog.effectiveFrom).toLocaleDateString('zh-CN') : '—'}</strong><small>{catalog?.notes}</small></article>
      </section>
      <section className="panel">
        <div className="panel-heading"><h2>产品与基准价</h2><span>{catalog?.label}</span></div>
        <div className="catalog-table">
          <div className="catalog-row header"><span>产品</span><span>规格</span><span>计价方式</span><span>税率</span><span>状态</span></div>
          {catalog?.products.map((product) => (
            <div className="catalog-row" key={product.id}>
              <span><strong>{product.name}</strong><small>{product.sku}</small></span>
              <span>{product.specification}</span>
              <span>{product.pricing.kind === 'fixed' ? formatMoney(product.pricing.unitPriceMinor) : `成本 + ${product.pricing.markupBps / 100}%`}</span>
              <span>{product.taxRateBps / 100}%</span>
              <span className="status published">有效</span>
            </div>
          ))}
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading"><h2>价格版本</h2><span>{versions.length} 个版本</span></div>
        <div className="version-list">
          {versions.map((version) => <div key={version.id}><span className="version-dot" /><strong>V{version.version}</strong><span>{version.label}</span><span>{new Date(version.effectiveFrom).toLocaleDateString('zh-CN')}</span><span className={`status ${version.status}`}>{version.status === 'published' ? '已发布' : version.status === 'draft' ? '草稿' : version.status === 'frozen' ? '已冻结' : '历史版本'}</span></div>)}
        </div>
      </section>
      <p className="permission-note">当前账号：{roleLabel(user)}。正式导入与发布接口按角色校验，所有操作写入审计日志。</p>
    </>
  );
}

function AuditPage() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState('');
  useEffect(() => { api.get<AuditEvent[]>('/audit').then(setEvents).catch((reason: Error) => setError(reason.message)); }, []);
  return (
    <>
      <div className="page-heading"><div><p className="eyebrow">不可覆盖的业务轨迹</p><h1>审计记录</h1><p>价格发布、报价审批、导出和权限事件按时间保留。</p></div></div>
      {error && <div className="alert error">{error}</div>}
      <section className="panel audit-list">
        {events.map((event) => <div className="audit-event" key={event.id}><span className="audit-time">{formatDate(event.createdAt)}</span><span className="audit-action">{event.action}</span><span>{event.entityType} / {event.entityId.slice(0, 8)}</span><span>{event.actorId}</span></div>)}
        {events.length === 0 && <div className="empty">暂无审计事件</div>}
      </section>
    </>
  );
}

export function App() {
  const [user, setUser] = useState<User>();
  const [view, setView] = useState<View>('dashboard');
  const [booting, setBooting] = useState(true);
  const [path, setPath] = useState(window.location.pathname);
  const localTestMode = import.meta.env.DEV || ['localhost', '127.0.0.1'].includes(window.location.hostname);

  const navigate = useCallback((nextPath: string) => {
    window.history.pushState({}, '', nextPath);
    setPath(nextPath);
  }, []);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    api.get<User>('/auth/me')
      .then((authenticatedUser) => {
        setUser(authenticatedUser);
        if (window.location.pathname === '/login') {
          setView('dashboard');
          navigate('/workbench');
        }
      })
      .catch(() => undefined)
      .finally(() => setBooting(false));
  }, [navigate]);


  function handleHomeSubmit(value: string) {
    sessionStorage.setItem('quotation.homeDraft', value);
    if (user) {
      setView('workspace');
      navigate('/workbench');
    }
  }

  const content = useMemo(() => {
    if (!user) return null;
    if (view === 'workspace') return <QuoteWorkspace user={user} />;
    if (view === 'quotes') return <QuoteCenter user={user} />;
    if (view === 'catalog') return <CatalogPage user={user} />;
    if (view === 'approvals') return <Approvals user={user} />;
    if (view === 'audit') return <AuditPage />;
    return <Dashboard user={user} onNew={() => setView('workspace')} />;
  }, [user, view]);

  if (booting) return <div className="boot-screen"><span className="brand-mark">报</span><p>正在连接报价服务…</p></div>;
  if (localTestMode && (path === '/test-workbench' || (path === '/workbench' && !user))) return <DevAutoLogin onLogin={(testUser) => { setUser(testUser); setView('workspace'); navigate('/workbench'); }} />;
  if (localTestMode && path === '/dev-login' && !user) return <DevLoginScreen onLogin={(testUser) => { setUser(testUser); setView('workspace'); navigate('/workbench'); }} />;
  if (path === '/') return <HomePage user={user} onLogin={() => { setView('workspace'); navigate('/workbench'); }} onSubmit={handleHomeSubmit} />;
  if (!user) return <LoginScreen />;
  return <AppShell user={user} view={view} onView={setView} onHome={() => { setView('dashboard'); navigate('/'); }} onLogout={() => { document.cookie = 'quotation_user=; Max-Age=0; path=/'; setUser(undefined); setView('dashboard'); navigate('/'); }}>{content}</AppShell>;
}
